// Display mirror of backend/src/lib/portfolioStatus.ts — how the consultant
// portfolio classifies a client. The server's summary tiles and the sidebar
// badge are computed from the backend copy; every list, pill and chart on the
// consultant pages uses this one. Change both together.
//
// The score is the client's SOA overall (backend lib/frameworkScores.ts), the
// figure the client sees on their own sidebar — never the last AI check.

export const SCORE_THRESHOLDS = { strong: 80, compliant: 75, partial: 50 } as const;

export type ScoreBand = "unassessed" | "non_compliant" | "partial" | "compliant" | "strong";

export function scoreBand(score: number | null): ScoreBand {
  if (score === null) return "unassessed";
  if (score >= SCORE_THRESHOLDS.strong) return "strong";
  if (score >= SCORE_THRESHOLDS.compliant) return "compliant";
  if (score >= SCORE_THRESHOLDS.partial) return "partial";
  return "non_compliant";
}

/** `strong` sits inside compliant: 75% and over. */
export function isCompliantScore(score: number | null): boolean {
  const b = scoreBand(score);
  return b === "compliant" || b === "strong";
}

/**
 * The band's name. "Scoring 80%+" rather than "Certified": no certification
 * record exists on the platform, and a score is not a certificate.
 */
export const SCORE_BAND_LABELS: Record<ScoreBand, string> = {
  unassessed: "Not assessed",
  non_compliant: "Non-Compliant",
  partial: "Partially Compliant",
  compliant: "Compliant",
  strong: "Scoring 80%+",
};

/** An open finding (as the Findings page displays it) or a pending tier request. */
export function clientNeedsAction(c: { openFindings: number; pendingTierRequests: number }): boolean {
  return c.openFindings > 0 || c.pendingTierRequests > 0;
}
